import * as React from "react";
import {
  Typography,
  Box,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";

// answerBlocks: optional. If present, render structured content (paragraphs + bullet lists). Else use answerEn.
const FAQ_CATEGORIES = [
  {
    id: "getting-started",
    titleKey: "faq.category.gettingStarted",
    titleEn: "Getting Started",
    items: [
      {
        questionKey: "faq.whatIsPlatform",
        questionEn: "What is the Chainz Partner platform?",
        answerBlocks: [
          {
            type: "p",
            text: "The Chainz Partner platform is your central hub for fulfillment and logistics with Chainz Solutions. It includes:",
          },
          {
            type: "ul",
            items: [
              "Dashboard — sales and performance metrics",
              "Products — inventory management",
              "Send Inventory — ship stock to our warehouse",
              "Inventory History — track inbounds",
              "Orders — view and manage fulfillment",
              "Returns & Exchanges — reverse logistics",
              "Finance — transactions and payouts",
              "Action Center — orders needing attention (e.g. missing shipping info or late delivery)",
            ],
          },
        ],
      },
      {
        questionKey: "faq.howConnectShopify",
        questionEn: "How do I connect my Shopify store?",
        answerBlocks: [
          { type: "p", text: "Go to Settings in the menu, open the Shopify Integration tab, then:" },
          {
            type: "ul",
            items: [
              "Press \"New Key\" to create a new API key",
              "Install the CHAINZ app from the Shopify App Store",
              "Add the API key (shown on that Settings tab) into the app",
              "From the Sync Products page in the app, select products you want to fulfill with Chainz and click \"Send to CHAINZ\"",
              "NB: Each Shopify variant must have a unique SKU before sending products to CHAINZ",
            ],
          },
          {
            type: "p",
            text: "After that, orders from your store are sent to your Chainz account automatically and inventory is synced—no manual sync needed for orders.",
          },
        ],
      },
      {
        questionKey: "faq.howUpdateProfile",
        questionEn: "How do I update my profile?",
        answerEn: "Go to Settings in the menu (or open your profile from the top bar). From there you can view and edit your account and brand details as allowed by the platform.",
      },
    ],
  },
  {
    id: "inventory",
    titleKey: "faq.category.inventory",
    titleEn: "Inventory",
    items: [
      {
        questionKey: "faq.howSendInventory",
        questionEn: "How do I send inventory to Chainz?",
        answerBlocks: [
          { type: "p", text: "Use Send Inventory (under Inventory in the menu). You can upload an Excel file with these columns:" },
          {
            type: "ul",
            items: [
              "Product SKU — must match a product already in your Chainz products list",
              "Quantity",
              "Is RTO (optional)",
            ],
          },
          {
            type: "p",
            text: "You can download a template from the same page. Only products that exist in your Products catalog with a positive quantity will be included. Submit the file to create the inbound request.",
          },
        ],
      },
    ],
  },
  {
    id: "orders-fulfillment",
    titleKey: "faq.category.ordersFulfillment",
    titleEn: "Orders & Fulfillment",
    items: [
      {
        questionKey: "faq.whatIsActionCenter",
        questionEn: "What is the Action Center?",
        answerBlocks: [
          { type: "p", text: "The Action Center shows orders that need your attention. It has two tabs:" },
          {
            type: "ul",
            items: [
              "Orders without shipping — orders missing shipping details that cannot be fulfilled until you provide or fix them",
              "Late delivery orders — orders past their expected delivery window",
            ],
          },
          {
            type: "p",
            text: "You can search, view details, and take action (e.g. add shipping info or cancel) so we can proceed or close the order.",
          },
        ],
      },
      {
        questionKey: "faq.howViewOrders",
        questionEn: "How do I view and manage orders?",
        answerBlocks: [
          { type: "p", text: "Go to Orders in the menu. You can:" },
          {
            type: "ul",
            items: [
              "Filter by date range, status, and search",
              "Click an order to open its details",
              "From the order detail page: handle fulfillment (edit fulfillment info), process a return, create an exchange, or duplicate the order",
              "Use order statistics and export on the Orders page",
            ],
          },
        ],
      },
      {
        questionKey: "faq.whyOrdersInvalid",
        questionEn: "Why are my orders INVALID?",
        answerBlocks: [
          {
            type: "p",
            text: "Orders are marked INVALID when required fulfillment data is missing or incorrect.",
          },
          {
            type: "ul",
            items: [
              "Phone number is required at checkout for every order",
              "Products in the order must map to synced Chainz products with valid SKUs",
              "Each product variant must have its own unique SKU",
            ],
          },
          {
            type: "p",
            text: "If an order is INVALID, fix the missing data in your store settings or product setup, then re-sync or recreate the order.",
          },
        ],
      },
    ],
  },
  {
    id: "returns-exchanges",
    titleKey: "faq.category.returnsExchanges",
    titleEn: "Returns & Exchanges",
    items: [
      {
        questionKey: "faq.whatIsBacktrack",
        questionEn: "What is Backtrack (Returns System)?",
        answerBlocks: [
          { type: "p", text: "Backtrack is Chainz's returns and exchanges system. If your account has it enabled, you get:" },
          {
            type: "ul",
            items: [
              "A customer-facing returns portal (URL shown on the Backtrack home page)",
              "Return Requests and Exchange Requests to manage",
              "Options to customize the experience and manage which products are eligible",
            ],
          },
          {
            type: "p",
            text: "Subscribe or manage your subscription from Returns System in the menu (Backtrack (New)). From the Backtrack home you can copy your portal URL, manage products, and customize or unsubscribe.",
          },
        ],
      },
      {
        questionKey: "faq.returnsVsBacktrack",
        questionEn: "What is the difference between Returns and Backtrack?",
        answerBlocks: [
          {
            type: "p",
            text: "Returns (in the menu) is the list of return orders—orders that have been or are being processed as returns. You can filter, search, and open each for details.",
          },
          {
            type: "p",
            text: "Backtrack (Returns System) is the full returns workflow: the customer portal where end customers request returns/exchanges, and the screens where you manage those requests (Return Requests, Exchange Requests) and settings.",
          },
          { type: "p", text: "Summary: Returns = list of return orders. Backtrack = end-to-end returns system and portal." },
        ],
      },
      {
        questionKey: "faq.howReturnsExchangesWork",
        questionEn: "How do returns and exchanges work?",
        answerBlocks: [
          { type: "p", text: "Return orders appear under Returns; exchange orders under Exchanges. Open any order to see status and details." },
          {
            type: "p",
            text: "If you use Backtrack: customers submit return or exchange requests via your portal; you manage them in Return Requests and Exchange Requests. All returns and exchanges follow your commercial policy and the Returns & Exchange Policy in Terms & Conditions. Chainz executes based on your instructions and authorized requests.",
          },
        ],
      },
    ],
  },
  {
    id: "finance",
    titleKey: "faq.category.finance",
    titleEn: "Finance",
    items: [
      {
        questionKey: "faq.howFinanceWorks",
        questionEn: "How does Finance work?",
        answerBlocks: [
          { type: "p", text: "The Finance page (Transactions) shows your financial activity. You can see:" },
          {
            type: "ul",
            items: [
              "Next payout date and countdown",
              "Total income, fees, hold, and payout",
              "Cash cycles and payouts",
              "A list of transactions (orders, fees, payouts, etc.) with filters by date range and cash-out cycle",
            ],
          },
          { type: "p", text: "Use the balance breakdown to see how your balance is calculated. You can export transaction data for your records." },
        ],
      },
    ],
  },
  {
    id: "help-desk",
    titleKey: "faq.category.helpDesk",
    titleEn: "Help Desk",
    items: [
      {
        questionKey: "faq.whatIsHelpDesk",
        questionEn: "What is the Help Desk?",
        answerBlocks: [
          {
            type: "p",
            text: "The Help Desk is where you get support from Chainz. You can open tickets for issues or questions, and our team will respond and work with you until the issue is resolved.",
          },
          {
            type: "p",
            text: "You can create tickets by type (General Inquiry, Order, Product, Inventory, or Finance), add a subject and description, and optionally attach images. Each ticket has a status (Pending, In Progress, Resolved, or Closed). You can view all your tickets, add comments, reply to support, and when a ticket is resolved you can rate your experience and leave optional feedback.",
          },
        ],
      },
      {
        questionKey: "faq.howCreateTicket",
        questionEn: "How do I create a support ticket?",
        answerBlocks: [
          { type: "p", text: "Go to Help Desk in the menu and click \"New Ticket\". Then:" },
          {
            type: "ul",
            items: [
              "Choose the ticket type (General Inquiry, Order, Product, Inventory, or Finance). For Order, Product, Inventory, or Finance you can optionally link a specific order, product, transaction, or inbound.",
              "Enter a subject and description (both required).",
              "Optionally add image attachments (e.g. screenshots).",
            ],
          },
          {
            type: "p",
            text: "You can also create a ticket directly from an order, transaction, product, or inbound: on the Orders, Finance (Transactions), Products, or Inventory History pages, use the \"Get Help\" button to open a new ticket already linked to that order, transaction, product, or inbound.",
          },
        ],
      },
      {
        questionKey: "faq.howTrackReplyTicket",
        questionEn: "How do I track or reply to a ticket?",
        answerBlocks: [
          { type: "p", text: "Open Help Desk from the menu. Your tickets are listed with subject, type, and status. Click a ticket to open its detail." },
          {
            type: "p",
            text: "In the ticket detail you see the full conversation (comments). You can add a new comment, reply to a specific message, and attach one image per comment. Support replies appear in the same thread. When your ticket is resolved, you can rate your experience (1–5) and optionally leave feedback.",
          },
        ],
      },
    ],
  },
  {
    id: "account-support",
    titleKey: "faq.category.accountSupport",
    titleEn: "Account & Support",
    items: [
      {
        questionKey: "faq.bostaFlexShip",
        questionEn: "What is Bosta FlexShip?",
        answerEn:
          "Open Settings, then Brand Info, and find Bosta FlexShip. Turn it on and set a collection amount in EGP. If the customer refuses the shipment, Bosta collects this amount. Chainz sends that amount on every Bosta delivery and every Bosta exchange — you do not enter it again when creating an order. Bosta only applies FlexShip when the customer is allowed to open the package, so Allow open package must stay on for the brand. The setting is for Bosta brands only.",
      },
      {
        questionKey: "faq.whatAreNotifications",
        questionEn: "What are notifications?",
        answerEn:
          "Notifications keep you updated on important events (e.g. orders, returns, or account updates). The bell icon in the top bar shows the number of unread notifications. Click it or open Notifications from the menu to see the full list and mark items as read.",
      },
      {
        questionKey: "faq.termsAndContact",
        questionEn: "Where can I read Terms & Conditions and who do I contact?",
        answerBlocks: [
          {
            type: "p",
            text: "Terms & Conditions and the Returns & Exchange Policy are available from the menu under Terms & Conditions (one page with two tabs).",
          },
          {
            type: "p",
            text: "For support or inquiries, use Contact Us in the menu or the contact channels listed on the Chainz website.",
          },
        ],
      },
    ],
  },
];

// Parses translated answer string: "Paragraph||• Bullet1\n• Bullet2||Paragraph2"
const parseTranslatedAnswer = (str) => {
  if (!str || typeof str !== "string") return null;
  const blocks = str.split("||").map((s) => s.trim()).filter(Boolean);
  return blocks.map((block) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    const bulletLines = lines.filter((l) => l.startsWith("•") || l.startsWith("-"));
    if (bulletLines.length > 0) {
      return {
        type: "ul",
        items: bulletLines.map((l) => l.replace(/^[•\-]\s*/, "").trim()),
      };
    }
    return { type: "p", text: block };
  });
};

const AnswerContent = ({ answerEn, answerBlocks, translatedAnswer, isMobile }) => {
  const content =
    translatedAnswer != null
      ? parseTranslatedAnswer(translatedAnswer)
      : answerBlocks || (answerEn ? [{ type: "p", text: answerEn }] : []);

  if (!content || content.length === 0) return null;

  const blockSx = {
    "& p": {
      margin: 0,
      marginBottom: 1.5,
      color: "var(--secColor4)",
      opacity: 0.92,
      lineHeight: 1.75,
      fontSize: isMobile ? "0.9rem" : "1rem",
    },
    "& ul": {
      margin: "0 0 12px 0",
      paddingLeft: 2.5,
    },
    "& li": {
      marginBottom: 6,
      color: "var(--secColor4)",
      opacity: 0.92,
      lineHeight: 1.65,
      fontSize: isMobile ? "0.9rem" : "1rem",
    },
  };

  return (
    <Box component="div" sx={blockSx}>
      {content.map((block, idx) =>
        block.type === "p" ? (
          <Typography key={idx} component="p" variant="body1" sx={{ whiteSpace: "pre-line" }}>
            {block.text}
          </Typography>
        ) : block.type === "ul" ? (
          <Box component="ul" key={idx} sx={{ listStyleType: "disc" }}>
            {block.items.map((bullet, i) => (
              <Typography key={i} component="li" variant="body1">
                {bullet}
              </Typography>
            ))}
          </Box>
        ) : null
      )}
    </Box>
  );
};

const FaqAccordionItem = ({ item, isMobile, categoryId }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const questionId = item.questionKey.split(".").pop();
  const questionKey = `faq.questions.${questionId}`;
  const answerKey = `faq.answers.${questionId}`;
  const question = t(questionKey) !== questionKey ? t(questionKey) : item.questionEn;
  const translatedAnswer = t(answerKey) !== answerKey ? t(answerKey) : null;
  const answerEn = item.answerEn;
  const answerBlocks = item.answerBlocks;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        backgroundColor: "var(--cardBg)",
        color: "var(--secColor4)",
        boxShadow: "none",
        border: "1px solid var(--borderColor)",
        borderRadius: "12px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
        marginBottom: 1.5,
        "&.Mui-expanded": { marginBottom: 1.5 },
        "& .MuiAccordionSummary-root": { minHeight: 64, paddingRight: 1 },
        "& .MuiAccordionSummary-content": { margin: "14px 0" },
        "& .MuiAccordionDetails-root": { paddingTop: 0, paddingBottom: 2.5, paddingInline: 3 },
      }}
    >
      <AccordionSummary
        expandIcon={
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "var(--primaryColor)",
              color: "var(--secColor4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </Box>
        }
        aria-controls={`faq-${categoryId}-${item.questionKey}`}
        id={`faq-${categoryId}-${item.questionKey}`}
      >
        <Typography fontWeight={600} sx={{ color: "var(--secColor4)", fontSize: isMobile ? "0.95rem" : "1.05rem", pr: 1 }}>
          {question}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <AnswerContent
          answerEn={answerEn}
          answerBlocks={answerBlocks}
          translatedAnswer={translatedAnswer}
          isMobile={isMobile}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export const FAQ = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: "100%",
        pb: 4,
        backgroundColor: "var(--appBg)",
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 4,
          pb: 2,
        }}
      >
        {/* Hero */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: "var(--secColor4)",
              fontWeight: 700,
              fontSize: isMobile ? "1.75rem" : "2rem",
              mb: 1,
              position: "relative",
              display: "inline-block",
            }}
          >
            {t("navigation.faqs")}
            <Box
              component="span"
              sx={{
                position: "absolute",
                bottom: -4,
                left: 0,
                right: 0,
                height: 6,
                borderRadius: 1,
                backgroundColor: "var(--primaryColor)",
                opacity: 0.45,
                zIndex: 0,
              }}
            />
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "var(--secColor4)",
              opacity: 0.85,
              maxWidth: 560,
              mx: "auto",
              lineHeight: 1.65,
              mt: 2,
            }}
          >
            {t("faq.intro") || "Common questions about the partner platform and how to use it."}
          </Typography>
        </Box>

        {/* Categories */}
        {FAQ_CATEGORIES.map((category) => {
          const title = t(category.titleKey) !== category.titleKey ? t(category.titleKey) : category.titleEn;
          return (
            <Box key={category.id} sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "var(--secColor4)",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  mb: 2,
                  pb: 0.5,
                  borderBottom: "2px solid var(--primaryColor)",
                  display: "inline-block",
                }}
              >
                {title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0, mt: 2 }}>
                {category.items.map((item, index) => (
                  <FaqAccordionItem key={index} item={item} isMobile={isMobile} categoryId={category.id} />
                ))}
              </Box>
            </Box>
          );
        })}
      </Container>
    </Box>
  );
};

export default FAQ;
